<template>
	<b-container fluid class="d-flex flex-column w-100 h-100 px-2">
		<b-row v-for="section in form.Sections" v-bind:key="section.Name" class="p-3 m-0">
			<b-col>
				<b-card no-body class="p-3">
					<b-form-group
						:label="section.Label"
						label-cols="12"
						label-cols-lg="12"
						label-class="font-weight-bold text-dark pt-0 pb-2"
						label-align-sm="left"
						label-size="lg"
						class="m-0" />
						<b-button-toolbar v-if="ConsultMode !== objectDisplayingModeEnum.Read" key-nav :aria-label="$t('ToolbarButtons')">
							<b-button-group class="pt-1 pb-1">
								 <!-- AddEvent : Ajouter un évènement -->
								<b-button v-b-tooltip.hover :title="$t('AddEvent')" @click="displayModal(undefined)" variant="outline-secondary" class="btn-circle-small"><b-icon icon="plus" /></b-button>
							</b-button-group>
						</b-button-toolbar>
					<b-card no-body class="w-100">
						<b-card-header header-tag="header" header-bg-variant="secondary" class="text-center text-white font-weight-bold pt-0 pb-0" role="tab">
							{{ section.Label }}
						</b-card-header>
						
						<!-- Construit la liste de champs -->
						<!-- <Fields v-bind:Fields="section.Fields.filter(x => x.Type !==  fieldTypeEnum.Table)" v-bind:ConsultMode="ConsultMode" /> -->

						<!-- Tableau des évènements -->
						<b-table v-for="table in section.Fields.filter(x => x.Type === fieldTypeEnum.Table)" :key="table['Name']" :fields="convertHeaders(table['Headers'])" :items="table.Value"
							striped bordered hover small tbody-tr-class="p-0" class="m-0">
							<template #head()="data">
								<div class="text-center">{{ data.label }}</div>
							</template>
							<template #cell(Date)="data">
								{{ data.value | formatDate('L') }}
							</template>

							<!-- Section contenant les boutons de mise-à-jour et suppression d'une ligne d'Event -->
							<template #cell(remove)="row">
								<div class="text-center">
									<!-- DeleteEvent : Supprimer l'évènement -->
									<b-button v-b-tooltip.hover :title="$t('DeleteEvent')" size="sm" pill variant="outline-danger" class="text-center p-0 m-0 border-0" @click="removeEvent(row.item.Id)"><font-awesome-icon icon="x" /></b-button>
								</div>
							</template>
							<template #cell(edit)="row">
								<div class="text-center">
									<!-- EditEvent : Modifier l'évènement -->
									<b-button v-b-tooltip.hover :title="$t('EditEvent')" size="sm" pill variant="outline-secondary" class="text-center p-0 m-0 border-0" @click="displayModal(row.item)"><font-awesome-icon icon="pen"/></b-button>
								</div>
							</template>
						</b-table>
					</b-card>
				</b-card>
			</b-col>
		</b-row>
	  <b-modal id="eventModal" v-model="displayEventModal" centered no-close-on-backdrop no-close-on-esc>
			<template #modal-header="{ }">
				<!-- Event : Evènement -->
				<h6>{{$t('Event')}}</h6>
			</template>
			<b-form-group
				:label="$t('Date')"
				label-cols-sm="4"
				label-for="eventDate"
				label-align-sm="left" class="mb-1">
				<b-input-group id="eventDate">
					<!-- @todo: gérer le format de la date selon que la culture soit française ou anglaise -->
					<DatePicker v-model="editedEventDate" :State.sync="eventState" :Variant="Variant" :Locale="$store.getters['SessionStore/GetLanguage']" />
					<!-- DateFormatError : La date saisie n'est pas dans le bon format -->
					<b-form-invalid-feedback :state="eventState" class="text-center m-0">
						{{$t('DateFormatError')}} ({{ navLanguage.slice(0, 2) == 'fr' ? "jj/mm/aaaa" : "YYYY-MM-DD" }})
					</b-form-invalid-feedback>
				</b-input-group>
			</b-form-group>
			<!-- Description : Description -->
			<b-form-group
				:label="$t('Description')"
				label-cols-sm="4"
				label-for="eventDescription"
				label-align-sm="left">
				<b-input-group id="eventDescription">
					<b-form-textarea
						v-model="editedEventDescription"
						rows="3"
						max-rows="15" />
				</b-input-group>
			</b-form-group>
			<template #modal-footer="{ cancel }">
				 <!-- Cancel : Annuler -->
				<b-button @click="cancel(); eventState = null" variant="outline-secondary" pill>{{$t('Cancel')}}</b-button>
				<!-- Validate : Valider -->
				<b-button @click="modifyMode ? updateEvent() : addEvent()" variant="secondary" pill :disabled="eventState == false" >{{$t('Validate')}}</b-button> 
			</template>
		</b-modal>
	</b-container>
</template>

<script lang="ts">
import { Component } from "vue-property-decorator";
import { Getter } from "vuex-class";
import Fields from "@/components/commons/Fields.vue";
import FieldTypeEnum from "@/enums/fields/FieldTypeEnum";
import TableHeader from "@/types/fields/TableHeader";
import moment from "moment";
import ObjectDisplayingModeEnum from "@/enums/ObjectDisplayingModeEnum";
import TableField from "@/types/fields/TableField";
import { BaseObjectForm } from "./BaseObjectForm";
import DatePicker from "@/components/commons/DatePicker.vue";
import locales from "./locales/Events.json";

/** Composant de gestion des évènements pour la fiche objet */
@Component({
	components: {
		Fields,
		DatePicker
	},
	i18n: { messages: locales }
})
export default class Events extends BaseObjectForm {
	/** Pour pouvoir utiliser l'enum FieldTypeEnum dans la section template */
	private fieldTypeEnum = FieldTypeEnum;
	private objectDisplayingModeEnum = ObjectDisplayingModeEnum;

	//#region Fenêtre modale de saisie d'un évènement
	private displayEventModal: boolean = false;
	private eventId:number = 0;
	private editedEventDate: Date = new Date();
	private editedEventDescription: string = "";
	private modifyMode: boolean = false;
	private eventState: boolean = null;
	//#endregion Fenêtre modale de saisie d'un évènement

	/** Langue du navigateur utilisé */
    private navLanguage = navigator.language;

	/**  Obtient le dernier Id d'objet temporaire disponible */
	@Getter('ObjectStore/GetTempObjectId')
	private tempObjectId!: number;

	/** 
	 * Convertie un TableHeader en header compatible avec le Bootstrap Vue.js b-Table utilisé dans ce composant
	 * @param headers: Liste de TableHeader à convertir
	 *  */
	private convertHeaders(headers: TableHeader[]) {
		let tab = headers.map(o => {
			return { key: o.Name, label: o.Label, sortable: true, thStyle: {} };
		});

		if (this.ConsultMode !== ObjectDisplayingModeEnum.Read) {	// Si on est en mode édition
			// Ajout d'une colonne Remove pour placer le bouton Remove
			tab.unshift({ key: "remove", label: "", sortable: false, thStyle: { width: "10px" } });

			// Ajout d'une colonne Edit pour placer le bouton Edit
			tab.push({ key: "edit", label: "", sortable: false, thStyle: { width: "10px" } });
		}
		// let objId = { key: "Id", label: "Id", sortable: false, thStyle: {} };
		// tab.push(objId);

		return tab;
	}

	/** Affichage de la fenêtre modal d'édition d'un évènement */
	private displayModal(item: EventStruct) {
		if (item) {
			this.modifyMode = true;
			this.eventId = item.Id;
			this.editedEventDate = new Date(item.Date);
			this.editedEventDescription = item.Description;
		}
		else {
			this.modifyMode = false;
			this.editedEventDate = new Date();
			this.editedEventDescription = "";
		}
		this.displayEventModal = true;
	}

	private addEvent() {
		/** Réserve un nouvel Id d'objet temporaire */
		this.$store.dispatch("ObjectStore/SetTempObjectId").then(response => {
			console.debug("Events - addEvent()");
			let newEvent: EventStruct = { Id: this.tempObjectId, Date: moment(this.editedEventDate).format('MM/DD/YYYY'), Description: this.editedEventDescription };
			this.form.Sections[0].Fields[0]['Value'].push(newEvent);

			this.displayEventModal = false;
		})
		.catch(err => {
			console.error(err);
			this.$bvToast.toast(this.$t('AnErrorOccurred').toString(), { // "Une erreur s'est produite."
				title: this.$t('Error').toString(), // Erreur
				autoHideDelay: 10000,
				variant: "danger",
				solid: true,
				appendToast: true,
				toaster: "b-toaster-bottom-right"
			});
		});
	}

	private updateEvent() {
		this.displayEventModal = false;
		let editEventIndex: number = this.form.Sections[0].Fields[0]['Value'].findIndex(x => x.Id == this.eventId);
		console.debug("Events - this.eventId : " + this.eventId + "editEventIndex : " + editEventIndex);

		this.form.Sections[0].Fields[0]['Value'][editEventIndex].Date = moment(this.editedEventDate).format('MM/DD/YYYY')  ;
		this.form.Sections[0].Fields[0]['Value'][editEventIndex].Description = this.editedEventDescription;
	}

	private removeEvent(id: number) {
		this.$bvModal.msgBoxConfirm(this.$t('DeleteEventConfirmation').toString(), { // Voulez-vous vraiment supprimer cet évènement ? Cette action sera irréversible.
			title: this.$t('DeleteEventTitle').toString(), okVariant: 'danger', okTitle: this.$t('Yes').toString(), cancelTitle: this.$t('No').toString(), hideHeaderClose: false, centered: true // Suppression évènement
			}).then(value => {
				if (value) {
					let index: number = this.form.Sections[0].Fields[0]['Value'].findIndex(x => x.Id == id);
					this.form.Sections[0].Fields[0]['Value'].splice(index, 1);
				}
			});
	}
}

/** Type de gestion d'un évènement (type du même nom que celui côté back server...) */
interface EventStruct {
	Id: number;
	/** Date de type string et non Date pour gérer format long français pour compatilibité avec le parser json utilisé côté server */
	Date: string;
	Description: string;
}
</script>

<style scoped>

</style>